import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ReaderAiActiveReference } from "@/types/agentic-chat";
import { FileText, ImageIcon, ScanText, X } from "lucide-react";

const CHIP_ICON = {
  none: FileText,
  selection: ScanText,
  file: FileText,
  visual: ImageIcon,
} as const;

type ReaderAiReferenceChipProps = {
  reference: ReaderAiActiveReference;
  compact?: boolean;
  onClear?: () => void;
};

export function ReaderAiReferenceChip({ reference, compact = false, onClear }: ReaderAiReferenceChipProps) {
  const Icon = CHIP_ICON[reference.kind];
  const isNone = reference.kind === "none";

  return (
    <div className={cn("flex items-center gap-2", compact && "gap-1.5")}>
      <span
        className={cn(
          "inline-flex min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold shadow-[0_10px_24px_rgba(148,163,184,0.12)] backdrop-blur-md",
          compact ? "px-2 py-0.5 text-[9px] shadow-none" : "sm:px-3",
          isNone
            ? "border-slate-200/85 bg-white/88 text-slate-600"
            : reference.kind === "visual"
              ? "border-fuchsia-100/85 bg-[linear-gradient(180deg,rgba(253,244,255,0.92),rgba(250,232,255,0.78))] text-fuchsia-700"
              : reference.kind === "file"
                ? "border-emerald-100/85 bg-[linear-gradient(180deg,rgba(236,253,245,0.92),rgba(209,250,229,0.74))] text-emerald-700"
                : "border-blue-100/85 bg-[linear-gradient(180deg,rgba(239,246,255,0.94),rgba(219,234,254,0.76))] text-primary",
        )}
      >
        <Icon className={cn("shrink-0", compact ? "h-3 w-3" : "h-3.5 w-3.5")} />
        <span className="truncate">{reference.label}</span>
      </span>
      {!isNone && onClear ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-full border border-slate-200/85 bg-white/88 text-slate-500 shadow-[0_8px_18px_rgba(148,163,184,0.12)] transition-colors hover:bg-white hover:text-slate-900",
            compact ? "h-6 w-6" : "h-7 w-7",
          )}
          onClick={onClear}
          aria-label="Clear active reader context"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
}
